/**
 * IndexedDB Storage Utility
 * 
 * Local storage for offline operation with automatic sync.
 */

const DB_NAME = 'ai-controller';
const DB_VERSION = 1;

// Store names
export const STORES = {
  CONFIG: 'config',
  DEVICES: 'devices',
  SENSORS: 'sensors',
  RULES: 'rules',
  ROOMS: 'rooms',
  PENDING_COMMANDS: 'pending_commands',
  SENSOR_READINGS: 'sensor_readings',
  LOGS: 'logs',
} as const;

type StoreName = typeof STORES[keyof typeof STORES];

let db: IDBDatabase | null = null;

/**
 * Initialize IndexedDB
 */
export async function initStorage(): Promise<IDBDatabase> {
  if (db) return db;
  
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onerror = () => {
      console.error('[Storage] Failed to open database:', request.error);
      reject(request.error);
    };
    
    request.onsuccess = () => {
      db = request.result;
      console.log('[Storage] Database opened');
      resolve(db);
    };
    
    request.onupgradeneeded = (event) => {
      const database = (event.target as IDBOpenDBRequest).result;
      console.log('[Storage] Upgrading database schema...');
      
      // Config store
      if (!database.objectStoreNames.contains(STORES.CONFIG)) {
        database.createObjectStore(STORES.CONFIG, { keyPath: 'key' });
      }
      
      // Devices store
      if (!database.objectStoreNames.contains(STORES.DEVICES)) {
        const store = database.createObjectStore(STORES.DEVICES, { keyPath: 'id' });
        store.createIndex('roomId', 'roomId', { unique: false });
        store.createIndex('type', 'type', { unique: false });
      }
      
      // Sensors store
      if (!database.objectStoreNames.contains(STORES.SENSORS)) {
        const store = database.createObjectStore(STORES.SENSORS, { keyPath: 'id' });
        store.createIndex('roomId', 'roomId', { unique: false });
      }
      
      // Rules store
      if (!database.objectStoreNames.contains(STORES.RULES)) {
        const store = database.createObjectStore(STORES.RULES, { keyPath: 'id' });
        store.createIndex('roomId', 'roomId', { unique: false });
        store.createIndex('enabled', 'enabled', { unique: false });
      }
      
      // Rooms store
      if (!database.objectStoreNames.contains(STORES.ROOMS)) {
        database.createObjectStore(STORES.ROOMS, { keyPath: 'id' });
      }
      
      // Pending commands (for offline queue)
      if (!database.objectStoreNames.contains(STORES.PENDING_COMMANDS)) {
        const store = database.createObjectStore(STORES.PENDING_COMMANDS, { 
          keyPath: 'id',
          autoIncrement: true,
        });
        store.createIndex('timestamp', 'timestamp', { unique: false });
        store.createIndex('status', 'status', { unique: false });
      }
      
      // Sensor readings cache
      if (!database.objectStoreNames.contains(STORES.SENSOR_READINGS)) {
        const store = database.createObjectStore(STORES.SENSOR_READINGS, { keyPath: 'id' });
        store.createIndex('sensorId', 'sensorId', { unique: false });
        store.createIndex('timestamp', 'timestamp', { unique: false });
      }
      
      // Local logs
      if (!database.objectStoreNames.contains(STORES.LOGS)) {
        const store = database.createObjectStore(STORES.LOGS, { 
          keyPath: 'id',
          autoIncrement: true,
        });
        store.createIndex('timestamp', 'timestamp', { unique: false });
        store.createIndex('severity', 'severity', { unique: false });
      }
    };
  });
}

/**
 * Get database instance
 */
async function getDB(): Promise<IDBDatabase> {
  if (!db) {
    return initStorage();
  }
  return db;
}

/**
 * Get item from store
 */
export async function getItem<T>(store: StoreName, key: string): Promise<T | undefined> {
  const database = await getDB();
  
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(store, 'readonly');
    const objectStore = transaction.objectStore(store);
    const request = objectStore.get(key);
    
    request.onsuccess = () => resolve(request.result as T | undefined);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Set item in store
 */
export async function setItem<T>(store: StoreName, item: T): Promise<void> {
  const database = await getDB();
  
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(store, 'readwrite');
    const objectStore = transaction.objectStore(store);
    const request = objectStore.put(item);
    
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

/**
 * Delete item from store
 */
export async function deleteItem(store: StoreName, key: string): Promise<void> {
  const database = await getDB();
  
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(store, 'readwrite');
    const objectStore = transaction.objectStore(store);
    const request = objectStore.delete(key);
    
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

/**
 * Get all items from store
 */
export async function getAll<T>(store: StoreName): Promise<T[]> {
  const database = await getDB();
  
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(store, 'readonly');
    const objectStore = transaction.objectStore(store);
    const request = objectStore.getAll();
    
    request.onsuccess = () => resolve(request.result as T[]);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Get items by index
 */
export async function getByIndex<T>(
  store: StoreName, 
  indexName: string, 
  value: IDBValidKey
): Promise<T[]> {
  const database = await getDB();
  
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(store, 'readonly');
    const objectStore = transaction.objectStore(store);
    const index = objectStore.index(indexName);
    const request = index.getAll(value);
    
    request.onsuccess = () => resolve(request.result as T[]);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Clear all items in store
 */
export async function clearStore(store: StoreName): Promise<void> {
  const database = await getDB();
  
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(store, 'readwrite');
    const objectStore = transaction.objectStore(store);
    const request = objectStore.clear();
    
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

/**
 * Bulk insert items
 */
export async function bulkInsert<T>(store: StoreName, items: T[]): Promise<void> {
  const database = await getDB();
  
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(store, 'readwrite');
    const objectStore = transaction.objectStore(store);
    
    items.forEach(item => objectStore.put(item));
    
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
}

// ============================================================================
// Pending Commands Queue (for offline operations)
// ============================================================================

export interface PendingCommand {
  id?: number;
  type: 'device_state' | 'device_mode' | 'device_level' | 'rule_toggle';
  targetId: string;
  payload: unknown;
  timestamp: Date;
  status: 'pending' | 'syncing' | 'completed' | 'failed';
  retries: number;
  error?: string;
}

/**
 * Add command to offline queue
 */
export async function queueCommand(
  type: PendingCommand['type'],
  targetId: string,
  payload: unknown
): Promise<void> {
  const command: PendingCommand = {
    type,
    targetId,
    payload,
    timestamp: new Date(),
    status: 'pending',
    retries: 0,
  };
  
  await setItem(STORES.PENDING_COMMANDS, command);
  console.log('[Storage] Command queued:', type, targetId);
}

/**
 * Get pending commands
 */
export async function getPendingCommands(): Promise<PendingCommand[]> {
  return getByIndex<PendingCommand>(STORES.PENDING_COMMANDS, 'status', 'pending');
}

/**
 * Update command status
 */
export async function updateCommandStatus(
  id: number,
  status: PendingCommand['status'],
  error?: string
): Promise<void> {
  const command = await getItem<PendingCommand>(STORES.PENDING_COMMANDS, String(id));
  if (command) {
    command.status = status;
    if (error) command.error = error;
    command.retries++;
    await setItem(STORES.PENDING_COMMANDS, command);
  }
}

/**
 * Sync pending commands with server
 */
export async function syncPendingCommands(
  apiCall: (command: PendingCommand) => Promise<void>
): Promise<{ success: number; failed: number }> {
  const pending = await getPendingCommands();
  let success = 0;
  let failed = 0;
  
  for (const command of pending) {
    try {
      await updateCommandStatus(command.id!, 'syncing');
      await apiCall(command);
      await updateCommandStatus(command.id!, 'completed');
      success++;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      await updateCommandStatus(command.id!, 'failed', message);
      failed++;
    }
  }
  
  console.log(`[Storage] Sync complete: ${success} success, ${failed} failed`);
  return { success, failed };
}

// ============================================================================
// Data Sync Helpers
// ============================================================================

/**
 * Cache data from API for offline use
 */
export async function cacheFromApi<T extends { id: string }>(
  store: StoreName,
  data: T[]
): Promise<void> {
  await clearStore(store);
  await bulkInsert(store, data);
  
  // Store last sync time
  await setItem(STORES.CONFIG, {
    key: `${store}_lastSync`,
    value: new Date().toISOString(),
  });
  
  console.log(`[Storage] Cached ${data.length} items in ${store}`);
}

/**
 * Get last sync time for a store
 */
export async function getLastSyncTime(store: StoreName): Promise<Date | null> {
  const config = await getItem<{ key: string; value: string }>(
    STORES.CONFIG,
    `${store}_lastSync`
  );
  return config ? new Date(config.value) : null;
}

/**
 * Check if data is stale (older than maxAge)
 */
export async function isDataStale(store: StoreName, maxAgeMs: number): Promise<boolean> {
  const lastSync = await getLastSyncTime(store);
  if (!lastSync) return true;
  
  return Date.now() - lastSync.getTime() > maxAgeMs;
}
